import { useState, useEffect, useCallback } from 'react';
import { Input } from '@/components/ui/input';
import { Checkbox } from '@/components/ui/checkbox';
import { Button } from '@/components/ui/button';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Loader2, Search, ChevronLeft, ChevronRight } from 'lucide-react';
import { apiFetch } from '@/lib/api';

interface Contact {
  id: string;
  // Nullable on the API — a contact imported from a spreadsheet may have neither. The rendering
  // below already handles both being absent; the types now say so too.
  name: string | null;
  phone: string | null;
  email?: string | null;
  propertyInterest?: string | null;
  source?: string;
  isUnsubscribed?: boolean;
}

interface ContactSelectorProps {
  workspaceId: string;
  selectedIds: string[];
  onSelectionChange: (ids: string[]) => void;
}

export default function ContactSelector({
  workspaceId,
  selectedIds,
  onSelectionChange,
}: ContactSelectorProps) {
  const [contacts, setContacts] = useState<Contact[]>([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [total, setTotal] = useState(0);
  const limit = 10;

  const fetchContacts = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({
        page: page.toString(),
        limit: limit.toString(),
      });
      if (search) params.append('search', search);

      const response = await apiFetch<{
        contacts: Contact[];
        pagination: { totalPages: number; total: number };
      }>(`/workspaces/${workspaceId}/contacts?${params}`);

      setContacts(response.contacts || []);
      setTotalPages(response.pagination?.totalPages || 1);
      setTotal(response.pagination?.total || 0);
    } catch (error) {
      console.error('Failed to fetch contacts:', error);
    } finally {
      setLoading(false);
    }
  }, [workspaceId, page, search]);

  // Debounced: without this, every character typed fired its own request.
  useEffect(() => {
    const timer = setTimeout(fetchContacts, 300);
    return () => clearTimeout(timer);
  }, [fetchContacts]);

  // A contact with no phone number, or one who has unsubscribed, cannot be messaged — the backend
  // filters both out of the recipient count and the send, even in 'selected' mode. Selecting one
  // used to appear to work and then silently do nothing, so they are excluded from selection here
  // instead, with the reason shown in place of the phone number.
  const canMessage = (contact: Contact) =>
    Boolean(contact.phone && contact.phone.trim()) && !contact.isUnsubscribed;

  const blockedReason = (contact: Contact) =>
    contact.isUnsubscribed
      ? 'Unsubscribed — cannot be messaged'
      : 'No phone number — cannot be messaged';

  const handleSelectAll = () => {
    const currentPageIds = contacts.filter(canMessage).map((c) => c.id);
    const allSelected =
      currentPageIds.length > 0 && currentPageIds.every((id) => selectedIds.includes(id));

    if (allSelected) {
      onSelectionChange(selectedIds.filter((id) => !currentPageIds.includes(id)));
    } else {
      const newIds = [...new Set([...selectedIds, ...currentPageIds])];
      onSelectionChange(newIds);
    }
  };

  const handleToggle = (id: string) => {
    if (selectedIds.includes(id)) {
      onSelectionChange(selectedIds.filter((s) => s !== id));
    } else {
      onSelectionChange([...selectedIds, id]);
    }
  };

  const currentPageIds = contacts.filter(canMessage).map((c) => c.id);
  const allCurrentSelected =
    currentPageIds.length > 0 && currentPageIds.every((id) => selectedIds.includes(id));

  return (
    <div className="space-y-4">
      <div className="relative">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
        <Input
          placeholder="Search contacts..."
          value={search}
          onChange={(e) => {
            setSearch(e.target.value);
            setPage(1);
          }}
          className="pl-10"
        />
      </div>

      {loading ? (
        <div className="flex items-center justify-center py-8">
          <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
        </div>
      ) : contacts.length === 0 ? (
        <div className="text-center py-8 text-muted-foreground">
          No contacts found
        </div>
      ) : (
        <>
          <div className="flex items-center justify-between px-2 py-2 bg-muted/50 rounded-lg">
            <label className="flex items-center gap-2 cursor-pointer">
              <Checkbox
                checked={allCurrentSelected}
                onCheckedChange={handleSelectAll}
              />
              <span className="text-sm font-medium">Select all on this page</span>
            </label>
            {contacts.length > currentPageIds.length && (
              <span className="text-xs text-amber-600">
                {contacts.length - currentPageIds.length} without a phone number
              </span>
            )}
            <span className="text-sm text-muted-foreground">
              {selectedIds.length} selected
            </span>
          </div>

          <ScrollArea className="h-[300px] rounded-md border">
            <div className="divide-y">
              {contacts.map((contact) => {
                const messageable = canMessage(contact);
                return (
                  <label
                    key={contact.id}
                    className={`flex items-center gap-3 p-3 ${
                      messageable
                        ? 'hover:bg-muted/50 cursor-pointer'
                        : 'opacity-60 cursor-not-allowed'
                    }`}
                  >
                    <Checkbox
                      checked={selectedIds.includes(contact.id)}
                      onCheckedChange={() => handleToggle(contact.id)}
                      disabled={!messageable}
                    />
                    <div className="flex-1 min-w-0">
                      <p className="font-medium truncate">
                        {contact.name || 'Unnamed Contact'}
                      </p>
                      <p className="text-sm text-muted-foreground truncate">
                        {messageable ? contact.phone : blockedReason(contact)}
                        {contact.propertyInterest && ` • ${contact.propertyInterest}`}
                      </p>
                    </div>
                  </label>
                );
              })}
            </div>
          </ScrollArea>

          <div className="flex items-center justify-between">
            <p className="text-sm text-muted-foreground">
              {total} contacts total
            </p>
            <div className="flex items-center gap-2">
              <Button
                variant="outline"
                size="sm"
                onClick={() => setPage((p) => Math.max(1, p - 1))}
                disabled={page === 1}
              >
                <ChevronLeft className="h-4 w-4" />
              </Button>
              <span className="text-sm">
                {page} / {totalPages}
              </span>
              <Button
                variant="outline"
                size="sm"
                onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                disabled={page === totalPages}
              >
                <ChevronRight className="h-4 w-4" />
              </Button>
            </div>
          </div>
        </>
      )}
    </div>
  );
}
